import { useEffect, useRef } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function EmberCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });

    let width = 0, height = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];
    let raf = 0;
    let running = true;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const target = Math.round(Math.min(90, Math.max(30, width / 22)));
      while (particles.length < target) particles.push(makeParticle(true));
      if (particles.length > target) particles.length = target;
    };

    const makeParticle = (initial = false) => {
      const life = 3000 + Math.random() * 4000;
      return {
        x: Math.random() * width,
        y: initial ? Math.random() * height : height + Math.random() * 40,
        vx: (Math.random() - 0.5) * 0.25,
        vy: -0.25 - Math.random() * 0.55,
        r: 0.9 + Math.random() * 1.8,
        life,
        age: initial ? Math.random() * life : 0,
        hue: 15 + Math.random() * 30,
        alphaBase: 0.35 + Math.random() * 0.5,
        wobble: Math.random() * Math.PI * 2,
      };
    };

    const step = (dt) => {
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter';
      for (const p of particles) {
        p.age += dt;
        p.wobble += dt * 0.003;
        p.x += p.vx + Math.sin(p.wobble) * 0.15;
        p.y += p.vy;
        if (p.age > p.life || p.y < -20) {
          Object.assign(p, makeParticle(false));
          continue;
        }
        const t = p.age / p.life;
        const fade = t < 0.15 ? t / 0.15 : (1 - t) / 0.85;
        const alpha = p.alphaBase * Math.max(0, Math.min(1, fade)) * (0.75 + 0.25 * Math.sin(p.wobble * 3));
        const r = p.r;
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 6);
        grad.addColorStop(0,   `hsla(${p.hue + 20}, 100%, 75%, ${alpha})`);
        grad.addColorStop(0.4, `hsla(${p.hue}, 100%, 55%, ${alpha * 0.55})`);
        grad.addColorStop(1,   `hsla(${p.hue}, 100%, 40%, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 6, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    let last = performance.now();
    const tick = (t) => {
      if (!running) return;
      const dt = Math.min(48, t - last);
      last = t;
      step(dt);
      raf = requestAnimationFrame(tick);
    };

    resize();
    window.addEventListener('resize', resize);
    raf = requestAnimationFrame(tick);

    const handleVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        last = performance.now();
        raf = requestAnimationFrame(tick);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return <canvas ref={canvasRef} className="ember-canvas" aria-hidden="true" />;
}
